import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { createArticleSchema } from "@/utils/schemaHelpers";

type SignGuide = {
  id:
    | "aries"
    | "tauro"
    | "geminis"
    | "cancer"
    | "leo"
    | "virgo"
    | "libra"
    | "escorpio"
    | "sagitario"
    | "capricornio"
    | "acuario"
    | "piscis";
  name: string;
  symbol: string;
  dates: string;
  element: "Fuego" | "Tierra" | "Aire" | "Agua";
  modality: "Cardinal" | "Fijo" | "Mutable";
  ruler: string;
  characteristics: string[];
  values: string[];
  defects: string[];
  compatible: string[];
  incompatible: string[];
  famous: string[];
};

const signs: SignGuide[] = [
  {
    id: "aries",
    name: "Aries",
    symbol: "♈",
    dates: "21 Mar – 19 Abr",
    element: "Fuego",
    modality: "Cardinal",
    ruler: "Marte",
    characteristics: [
      "Directo, valiente y con iniciativa.",
      "Le gusta empezar cosas y tomar la delantera.",
      "Energía alta: cuando se entusiasma, arrastra a todos.",
    ],
    values: ["Coraje", "Autenticidad", "Acción", "Liderazgo"],
    defects: ["Impulsividad", "Impaciencia", "Competitividad excesiva"],
    compatible: ["Leo", "Sagitario", "Géminis", "Acuario"],
    incompatible: ["Cáncer", "Capricornio"],
    famous: ["Lady Gaga", "Robert Downey Jr.", "Emma Watson"],
  },
  {
    id: "tauro",
    name: "Tauro",
    symbol: "♉",
    dates: "20 Abr – 20 May",
    element: "Tierra",
    modality: "Fijo",
    ruler: "Venus",
    characteristics: [
      "Constante, sensorial y confiable.",
      "Construye de a poco: prefiere seguridad a improvisación.",
      "Aprecia lo bello, lo cómodo y lo bien hecho.",
    ],
    values: ["Lealtad", "Estabilidad", "Practicidad", "Disfrute"],
    defects: ["Terquedad", "Apego", "Resistencia al cambio"],
    compatible: ["Virgo", "Capricornio", "Cáncer", "Piscis"],
    incompatible: ["Acuario", "Leo"],
    famous: ["Adele", "Dwayne Johnson", "David Beckham"],
  },
  {
    id: "geminis",
    name: "Géminis",
    symbol: "♊",
    dates: "21 May – 20 Jun",
    element: "Aire",
    modality: "Mutable",
    ruler: "Mercurio",
    characteristics: [
      "Curioso, conversador y mentalmente rápido.",
      "Necesita variedad: aprende y conecta ideas.",
      "Se adapta fácil y tiene humor ágil.",
    ],
    values: ["Comunicación", "Libertad", "Ingenio", "Aprendizaje"],
    defects: ["Dispersión", "Inconstancia", "Sobrepensar"],
    compatible: ["Libra", "Acuario", "Aries", "Leo"],
    incompatible: ["Virgo", "Piscis"],
    famous: ["Angelina Jolie", "Johnny Depp", "Kanye West"],
  },
  {
    id: "cancer",
    name: "Cáncer",
    symbol: "♋",
    dates: "21 Jun – 22 Jul",
    element: "Agua",
    modality: "Cardinal",
    ruler: "La Luna",
    characteristics: [
      "Emocional, protector y muy intuitivo.",
      "Crea hogar: necesita pertenencia y seguridad afectiva.",
      "Recuerda detalles y cuida a los suyos.",
    ],
    values: ["Familia", "Cuidado", "Empatía", "Lealtad"],
    defects: ["Hipersenibilidad", "Cambios de humor", "Apego"],
    compatible: ["Tauro", "Virgo", "Escorpio", "Piscis"],
    incompatible: ["Aries", "Libra"],
    famous: ["Selena Gomez", "Tom Hanks", "Ariana Grande"],
  },
  {
    id: "leo",
    name: "Leo",
    symbol: "♌",
    dates: "23 Jul – 22 Ago",
    element: "Fuego",
    modality: "Fijo",
    ruler: "El Sol",
    characteristics: [
      "Carismático, creativo y expresivo.",
      "Le encanta brillar y también hacer brillar a otros.",
      "Tiene orgullo sano cuando se siente valorado.",
    ],
    values: ["Generosidad", "Creatividad", "Confianza", "Lealtad"],
    defects: ["Ego", "Dramatismo", "Necesidad de reconocimiento"],
    compatible: ["Aries", "Sagitario", "Libra", "Géminis"],
    incompatible: ["Tauro", "Escorpio"],
    famous: ["Jennifer Lopez", "Barack Obama", "Madonna"],
  },
  {
    id: "virgo",
    name: "Virgo",
    symbol: "♍",
    dates: "23 Ago – 22 Sep",
    element: "Tierra",
    modality: "Mutable",
    ruler: "Mercurio",
    characteristics: [
      "Analítico, detallista y servicial.",
      "Ordena el caos: ve mejoras donde otros no.",
      "Su amor se nota en lo práctico: resolver, cuidar, optimizar.",
    ],
    values: ["Responsabilidad", "Claridad", "Servicio", "Mejora continua"],
    defects: ["Perfeccionismo", "Crítica", "Ansiedad"],
    compatible: ["Tauro", "Capricornio", "Cáncer", "Escorpio"],
    incompatible: ["Géminis", "Sagitario"],
    famous: ["Beyoncé", "Keanu Reeves", "Zendaya"],
  },
  {
    id: "libra",
    name: "Libra",
    symbol: "♎",
    dates: "23 Sep – 22 Oct",
    element: "Aire",
    modality: "Cardinal",
    ruler: "Venus",
    characteristics: [
      "Diplomático, encantador y social.",
      "Busca equilibrio: justicia, armonía y acuerdos.",
      "Se inspira con lo estético y lo vincular.",
    ],
    values: ["Armonía", "Justicia", "Cooperación", "Estética"],
    defects: ["Indecisión", "Evitar conflicto", "Complacer de más"],
    compatible: ["Géminis", "Acuario", "Leo", "Sagitario"],
    incompatible: ["Cáncer", "Capricornio"],
    famous: ["Kim Kardashian", "Will Smith", "Bruno Mars"],
  },
  {
    id: "escorpio",
    name: "Escorpio",
    symbol: "♏",
    dates: "23 Oct – 21 Nov",
    element: "Agua",
    modality: "Fijo",
    ruler: "Plutón (tradicional: Marte)",
    characteristics: [
      "Intenso, magnético y profundo.",
      "No se queda en la superficie: busca verdad y lealtad.",
      "Transformador: renace después de crisis.",
    ],
    values: ["Lealtad", "Verdad", "Intensidad", "Resiliencia"],
    defects: ["Celos", "Control", "Desconfianza"],
    compatible: ["Cáncer", "Piscis", "Virgo", "Capricornio"],
    incompatible: ["Leo", "Acuario"],
    famous: ["Drake", "Katy Perry", "Leonardo DiCaprio"],
  },
  {
    id: "sagitario",
    name: "Sagitario",
    symbol: "♐",
    dates: "22 Nov – 21 Dic",
    element: "Fuego",
    modality: "Mutable",
    ruler: "Júpiter",
    characteristics: [
      "Optimista, aventurero y filosófico.",
      "Necesita libertad: explora, viaja, aprende.",
      "Tiene franqueza que a veces pica, pero rara vez es maldad.",
    ],
    values: ["Libertad", "Honestidad", "Crecimiento", "Sentido"],
    defects: ["Imprudencia", "Exceso", "Falta de tacto"],
    compatible: ["Aries", "Leo", "Libra", "Acuario"],
    incompatible: ["Virgo", "Piscis"],
    famous: ["Taylor Swift", "Brad Pitt", "Jay-Z"],
  },
  {
    id: "capricornio",
    name: "Capricornio",
    symbol: "♑",
    dates: "22 Dic – 19 Ene",
    element: "Tierra",
    modality: "Cardinal",
    ruler: "Saturno",
    characteristics: [
      "Ambicioso, disciplinado y estratégico.",
      "Piensa a largo plazo: construye con paciencia.",
      "Serio por fuera, sensible por dentro.",
    ],
    values: ["Disciplina", "Responsabilidad", "Estructura", "Perseverancia"],
    defects: ["Rigidez", "Frialdad aparente", "Autoexigencia"],
    compatible: ["Tauro", "Virgo", "Escorpio", "Piscis"],
    incompatible: ["Aries", "Libra"],
    famous: ["Michelle Obama", "Jim Carrey", "Denzel Washington"],
  },
  {
    id: "acuario",
    name: "Acuario",
    symbol: "♒",
    dates: "20 Ene – 18 Feb",
    element: "Aire",
    modality: "Fijo",
    ruler: "Urano (tradicional: Saturno)",
    characteristics: [
      "Independiente, innovador y humanitario.",
      "Piensa distinto: ve patrones y futuro.",
      "Le importa la amistad y los ideales.",
    ],
    values: ["Libertad", "Originalidad", "Amistad", "Causa colectiva"],
    defects: ["Distancia emocional", "Terquedad", "Rebeldía"],
    compatible: ["Géminis", "Libra", "Aries", "Sagitario"],
    incompatible: ["Tauro", "Escorpio"],
    famous: ["Oprah Winfrey", "Harry Styles", "Shakira"],
  },
  {
    id: "piscis",
    name: "Piscis",
    symbol: "♓",
    dates: "19 Feb – 20 Mar",
    element: "Agua",
    modality: "Mutable",
    ruler: "Neptuno (tradicional: Júpiter)",
    characteristics: [
      "Empático, soñador y artístico.",
      "Percibe lo sutil: emociones, ambientes, intenciones.",
      "Compasivo: se entrega cuando ama.",
    ],
    values: ["Compasión", "Imaginación", "Sensibilidad", "Espiritualidad"],
    defects: ["Idealización", "Evasión", "Falta de límites"],
    compatible: ["Cáncer", "Escorpio", "Tauro", "Capricornio"],
    incompatible: ["Géminis", "Sagitario"],
    famous: ["Rihanna", "Albert Einstein", "Justin Bieber"],
  },
];

const GuiaSignos = () => {
  const title = "Guía de los Signos del Zodiaco: características y compatibilidad";
  const description =
    "Guía completa de los 12 signos del zodiaco: Aries, Tauro, Géminis, Cáncer, Leo, Virgo, Libra, Escorpio, Sagitario, Capricornio, Acuario y Piscis. Características, valores, defectos, signos compatibles e incompatibles y ejemplos de famosos.";
  const url = `${window.location.origin}/guia-signos`;

  const articleSchema = createArticleSchema(title, description, url);
  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Guía de Signos" },
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
      </Helmet>

      <SchemaMarkup schema={articleSchema} />

      <div className="min-h-screen">
        <Navigation />

        <main className="pt-24 pb-12 px-4 text-left">
          <div className="container mx-auto max-w-5xl">
            <Breadcrumb items={breadcrumbItems} />

            <header className="mb-10">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-glow">
                Guía de los 12 Signos del Zodiaco
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed break-words hyphens-auto [text-wrap:pretty]">
                Una hoja completa (y fácil de leer) con características, valores, defectos, compatibilidades e
                incompatibilidades de cada signo: Aries, Tauro, Géminis, Cáncer, Leo, Virgo, Libra, Escorpio,
                Sagitario, Capricornio, Acuario y Piscis.
              </p>
            </header>

            <section aria-label="Índice de signos" className="mb-10">
              <Card className="p-4 sm:p-6 gradient-celestial">
                <h2 className="text-2xl font-bold mb-4">Índice rápido</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {signs.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="rounded-md px-3 py-2 bg-card/50 hover:bg-card/70 transition-colors border border-border/40 text-left"
                    >
                      <span className="font-bold">{s.symbol} {s.name}</span>
                      <span className="block text-xs text-muted-foreground">{s.dates}</span>
                    </a>
                  ))}
                </div>
              </Card>
            </section>

            <article className="space-y-10">
              {signs.map((s) => (
                <section
                  key={s.id}
                  id={s.id}
                  aria-label={`Guía del signo ${s.name}`}
                  className="scroll-mt-28"
                >
                  <Card className="p-4 sm:p-6 md:p-8 gradient-mystic">
                    <header className="mb-6">
                      <h2 className="text-3xl font-bold flex items-center gap-3">
                        <span className="text-4xl" aria-hidden="true">{s.symbol}</span>
                        {s.name}
                      </h2>
                       <p className="text-muted-foreground break-words hyphens-auto [text-wrap:pretty]">
                         {s.dates} • Elemento: {s.element} • Modalidad: {s.modality} • Regente: {s.ruler}
                       </p>
                    </header>

                    <div className="grid lg:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="text-xl font-bold text-primary">Características</h3>
                        <ul className="space-y-2 text-muted-foreground list-disc pl-5 marker:text-muted-foreground">
                          {s.characteristics.map((item) => (
                            <li key={item} className="break-words hyphens-auto [text-wrap:pretty]">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-xl font-bold text-secondary">Valores</h3>
                        <ul className="space-y-2 text-muted-foreground list-disc pl-5 marker:text-muted-foreground">
                          {s.values.map((item) => (
                            <li key={item} className="break-words hyphens-auto [text-wrap:pretty]">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6 mt-8">
                      <div className="space-y-4">
                        <h3 className="text-xl font-bold text-accent">Defectos</h3>
                        <ul className="space-y-2 text-muted-foreground list-disc pl-5 marker:text-muted-foreground">
                          {s.defects.map((item) => (
                            <li key={item} className="break-words hyphens-auto [text-wrap:pretty]">
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="space-y-4">
                        <h3 className="text-xl font-bold">Compatibilidad</h3>
                        <div className="space-y-3 text-muted-foreground">
                          <p className="break-words hyphens-auto [text-wrap:pretty]">
                            <span className="font-bold text-primary">Compatibles:</span> {s.compatible.join(", ")}
                          </p>
                          <p className="break-words hyphens-auto [text-wrap:pretty]">
                            <span className="font-bold text-secondary">Incompatibles:</span> {s.incompatible.join(", ")}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h3 className="text-xl font-bold mb-3">Personajes famosos (ejemplos)</h3>
                      <p className="text-muted-foreground break-words hyphens-auto [text-wrap:pretty]">
                        {s.famous.join(" • ")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Nota: los signos se basan en el Sol natal; en astrología, la carta completa (ascendente y luna) también influye.
                      </p>
                    </div>
                  </Card>
                </section>
              ))}
            </article>
          </div>
        </main>
      </div>
    </>
  );
};

export default GuiaSignos;
